import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { breadcrumbSchemaJson } from '../../utils/breadcrumbSchema';
import './Appointment.css';
import axiosInstance from '../../api/axiosInstance';
import { trackEvent } from '../../utils/trackEvent';
import useClientReady from '../../utils/useClientReady';
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

// Pure calculation, no side effects — safe to call during render. Kept outside
// the component so the initial useState call below can compute the first
// month's grid synchronously instead of leaving it empty until a post-mount
// effect runs, which previously caused a React 18 hydration mismatch on every
// page that embeds this component (prerendered HTML has a populated calendar;
// the pre-effect first client render did not).
const computeCalendarDays = (currentDate) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();
  const newDays = [];

  for (let i = 0; i < startingDay; i++) {
    newDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    newDays.push({
      day,
      isToday: new Date(year, month, day).toDateString() === new Date().toDateString(),
      isWeekend: [5, 6].includes(new Date(year, month, day).getDay()),
    });
  }

  return newDays;
};

const Appointment = ({ isAppointmentPage = false }) => {
  // The calendar shows today's date and month, which is different on the day a visitor loads the page than on
  // the day the prerender snapshot was taken, so it only renders once the page is live in a browser.
  const calendarReady = useClientReady();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [days, setDays] = useState(() => computeCalendarDays(new Date()));
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDays(computeCalendarDays(currentDate));
  }, [currentDate]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  const selectDate = (day) => {
    if (day) {
      const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 12);
      setSelectedDate(selected);
      console.log('Selected Date:', selected.toISOString().split('T')[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    if (!selectedDate) {
      setErrorMessage('Please select a date.');
      setIsLoading(false);
      return;
    }

    data.date_selected = selectedDate.toISOString().split('T')[0];
    console.log('Submitted Date:', data.date_selected);

    try {
      const response = await axiosInstance.post('api/appointments', data);
      trackEvent('appointment_submitted');
      setIsLoading(false);
      setShowModal(true);
      e.target.reset();
      setSelectedDate(null);
      setDays(computeCalendarDays(currentDate));
      setErrorMessage(null);
    } catch (error) {
      setIsLoading(false);
      const errorMsg = error.response?.data?.date_selected
        ? error.response.data.date_selected[0]
        : 'An error occurred while submitting the appointment.';
      setErrorMessage(errorMsg);
      console.error('Error submitting appointment:', error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const clearError = () => {
    setErrorMessage(null);
  };

  return (
    <div className="main_div_appointment">
      {isAppointmentPage && (
        <Helmet>
          <title>Schedule an Appointment | 360 SynergyTech</title>
          <meta
            name="description"
            content="Schedule a free consultation with 360 SynergyTech's experts to discuss your web development, AI, cloud, or CRM project."
          />
          <link rel="canonical" href="https://360synergytech.com/appointment" />

          <meta property="og:title" content="Schedule an Appointment | 360 SynergyTech" />
          <meta property="og:description" content="Schedule a free consultation with 360 SynergyTech's experts to discuss your web development, AI, cloud, or CRM project." />
          <meta property="og:url" content="https://360synergytech.com/appointment" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Schedule an Appointment | 360 SynergyTech" />
          <meta name="twitter:description" content="Schedule a free consultation with 360 SynergyTech's experts to discuss your web development, AI, cloud, or CRM project." />
          <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />
          <script type="application/ld+json">{breadcrumbSchemaJson([{ name: "Schedule an Appointment", path: "/appointment" }])}</script>
        </Helmet>
      )}
      {isAppointmentPage ? (
        <h1 className="appointment_heading" style={{ lineHeight: "inherit" }}>
          <strong>Schedule Your Appointment</strong>
        </h1>
      ) : (
        <div className="appointment_heading">
          <strong>Schedule Your Appointment</strong>
        </div>
      )}
      <div className="main_calender_appointment">
        <div className="appointment_info">
          We are here to help you with experts, Mon - Fri: 09.00am - 10.00 pm
        </div>
        <div className="appointment_content">
          <div className="appointment_form">
            <form id="appointmentForm" onSubmit={handleSubmit}>
              <div className="form_group">
                <label htmlFor="name">Full Name</label>
                <input type="text" id="name" name="name" placeholder="Enter your name" required />
              </div>
              <div className="form_group">
                <label htmlFor="email">Email Address</label>
                <input type="email" id="email" name="email" placeholder="Enter your email" required />
              </div>
              <div className="form_group">
                <label htmlFor="phone_number">Phone Number</label>
                <input type="tel" id="phone_number" name="phone_number" placeholder="Enter your phone" required />
              </div>
              <div className="form_group">
                <label htmlFor="website">Website (Optional)</label>
                <input type="url" id="website" name="website" placeholder="Enter your website" />
              </div>
              <button type="submit" className="schedule_meeting_btn" disabled={isLoading}>
                {isLoading ? (
                  <span className="spinner"></span>
                ) : (
                  'Schedule Now'
                )}
              </button>
            </form>
          </div>
          <div className="appointment_calendar">
            {calendarReady ? (
            <div className="calendar_container">
              <div className="calendar_nav">
                <button id="prev-month" type="button" onClick={prevMonth} aria-label="Previous month"><IoChevronBack aria-hidden="true" /></button>
                <span id="month-year">{`${currentDate.toLocaleString('en-US', { month: 'long' })} ${currentDate.getFullYear()}`}</span>
                <button id="next-month" type="button" onClick={nextMonth} aria-label="Next month"><IoChevronForward aria-hidden="true" /></button>
              </div>
              <div id="calendar-days" className="calendar-grid">
                {days.map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-day ${day && day.isToday ? 'today' : ''} ${day && day.isWeekend ? 'weekend' : ''} ${selectedDate && day && day.day === selectedDate.getDate() && currentDate.getMonth() === selectedDate.getMonth() && currentDate.getFullYear() === selectedDate.getFullYear() ? 'selected' : ''}`}
                    onClick={() => day && selectDate(day.day)}
                  >
                    {day ? day.day : ''}
                  </div>
                ))}
              </div>
            </div>
            ) : (
              <div className="calendar_container" aria-hidden="true" style={{ minHeight: "340px" }} />
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <div className="modal">
          <div className="modal_content">
            <h2>Success!</h2>
            <p>Appointment successfully submitted! Thank you.</p>
            <button onClick={closeModal} className="modal_close_btn">Close</button>
          </div>
        </div>
      )}
      {errorMessage && (
        <div className="toast">
          <p>{errorMessage}</p>
          <button onClick={clearError} className="toast_close">×</button>
        </div>
      )}
    </div>
  );
};

export default Appointment;